import { Listing } from '@df/catalog-ui/listing/listing';
import { ListingSort } from '@df/catalog-ui/listing/sort/listing-sort';
import { IListingSortOptionAttribute, ListingSortAttributeEngine } from '@df/catalog-ui/listing/sort/listing-sort-attribute-engine';
import { Model } from '@df/catalog/model';
import { Logger } from '@df/core/service/logger';
import { NgInject } from '@df/ng/ng-inject';

export class ListingSortAttributeEngineDecorator<T extends Model = Model> extends ListingSortAttributeEngine {
    @NgInject(Logger)
    private logger!: Logger;

    public override sort(collection: T[], option: IListingSortOptionAttribute, listing: Listing, cb: (models: T[]) => void): void {
        const heroProducts = listing.config?.source?.data?.heroProducts || [];

        if (heroProducts.length) {
            // push hero products to the bottom before setting up their final position,
            // its needed because otherwise we can remove products form top after setting other hero position and breaking the order this way
            collection = collection?.sort((a, b) => {
                if (heroProducts.find(i => i.product_id === a.id)) return 1;
                if (heroProducts.find(i => i.product_id === b.id)) return -1;
                return 0;
            });
        }

        // Apply hero product positioning with validation
        const validHeroProducts = heroProducts.filter(heroProduct =>
            this.validateHeroProductPosition(heroProduct, collection.length)
        );

        if (validHeroProducts.length !== heroProducts.length) {
            this.logger.warn('Some hero products were filtered out due to invalid position data', {
                totalHeroProducts: heroProducts.length,
                validHeroProducts: validHeroProducts.length,
                categoryId: listing.config?.source?.id
            });
        }

        validHeroProducts.forEach(heroProduct => {
            try {
                const matchingProduct = collection.find(product => product.id === heroProduct.product_id);

                if (matchingProduct) {
                    const insertIndex = heroProduct.position - 1;
                    const currentIndex = collection.indexOf(matchingProduct);

                    if (currentIndex >= 0) {
                        collection.splice(currentIndex, 1);
                        collection.splice(insertIndex, 0, matchingProduct);

                        this.logger.debug('Hero product positioned successfully', {
                            productId: heroProduct.product_id,
                            position: heroProduct.position,
                            insertIndex
                        });
                    }
                }
            } catch (error) {
                this.logger.error('Error positioning hero product', {
                    heroProduct,
                    error: error instanceof Error ? error.message : String(error),
                    categoryId: listing.config?.source?.id
                });
            }
        });

        super.sort(collection, option, listing, cb);
    }

    /**
     * Validates hero product position data
     * @param heroProduct The hero product to validate
     * @param maxPosition Maximum valid position (collection length)
     * @returns true if valid, false otherwise
     */
    private validateHeroProductPosition(heroProduct: {
        product_id: number;
        position: number;
    }, maxPosition: number): boolean {
        if (!heroProduct) {
            this.logger.warn('Hero product is null or undefined', { heroProduct });
            return false;
        }

        if (!heroProduct.product_id) {
            this.logger.warn('Hero product missing product_id', { heroProduct });
            return false;
        }

        if (typeof heroProduct.position !== 'number' || heroProduct.position < 1) {
            this.logger.warn('Hero product has invalid position (must be positive number)', {
                heroProduct,
                position: heroProduct.position
            });
            return false;
        }

        if (heroProduct.position > maxPosition) {
            this.logger.warn('Hero product position exceeds maximum allowed position', {
                heroProduct,
                position: heroProduct.position,
                maxPosition
            });
            return false;
        }

        return true;
    }
}

ListingSort.engines['attribute'] = new ListingSortAttributeEngineDecorator();
